/**
 * TanStack Query bindings for the engine. Every engine read is keyed by [method, params] so a change notification
 * can invalidate by method prefix. Mutations invalidate everything by default; pass `invalidate` to narrow it.
 */
import { useEffect } from "react";
import { QueryClient, useMutation, useQuery, useQueryClient, type UseMutationOptions, type UseMutationResult, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import type { EngineMethodName, EngineNotification, EngineParams, EngineResult } from "@hub/shared";
import { getEngine, subscribeEngine } from "./transport";
import { toast } from "@/ui";

let client: QueryClient | null = null;

export function createEngineQueryClient(): QueryClient {
  client = new QueryClient({
    defaultOptions: {
      queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: false },
    },
  });
  return client;
}

export function setEngineQueryClient(qc: QueryClient) {
  client = qc;
}

export function engineKey<M extends EngineMethodName>(method: M, params?: EngineParams<M>): unknown[] {
  return params === undefined ? ["engine", method] : ["engine", method, params];
}

export async function invokeEngine<M extends EngineMethodName>(method: M, params: EngineParams<M>): Promise<EngineResult<M>> {
  const engine = await getEngine();
  return engine.invoke(method, params);
}

export function useEngineQuery<M extends EngineMethodName>(
  method: M,
  params: EngineParams<M>,
  opts?: Omit<UseQueryOptions<EngineResult<M>, Error, EngineResult<M>, unknown[]>, "queryKey" | "queryFn">,
): UseQueryResult<EngineResult<M>, Error> {
  return useQuery<EngineResult<M>, Error, EngineResult<M>, unknown[]>({
    queryKey: engineKey(method, params),
    queryFn: () => invokeEngine(method, params),
    ...opts,
  });
}

export function useEngineMutation<M extends EngineMethodName>(
  method: M,
  opts?: Omit<UseMutationOptions<EngineResult<M>, Error, EngineParams<M>>, "mutationFn"> & { invalidate?: EngineMethodName[] | false; silent?: boolean },
): UseMutationResult<EngineResult<M>, Error, EngineParams<M>> {
  const qc = useQueryClient();
  const { invalidate, silent, ...rest } = opts ?? {};
  return useMutation<EngineResult<M>, Error, EngineParams<M>>({
    mutationFn: (params) => invokeEngine(method, params),
    ...rest,
    onSuccess: (data, vars, ctx) => {
      if (invalidate === false) {
        // caller handles invalidation
      } else if (invalidate) invalidate.forEach((m) => qc.invalidateQueries({ queryKey: ["engine", m] }));
      else qc.invalidateQueries({ queryKey: ["engine"] });
      (rest.onSuccess as ((d: EngineResult<M>, v: EngineParams<M>, c: unknown) => unknown) | undefined)?.(data, vars, ctx);
    },
    onError: (err, vars, ctx) => {
      if (!silent) toast(err.message || "Something went wrong", "danger");
      (rest.onError as ((e: Error, v: EngineParams<M>, c: unknown) => unknown) | undefined)?.(err, vars, ctx);
    },
  });
}

export function invalidateEngine(prefix?: EngineMethodName | EngineMethodName[]) {
  if (!client) return;
  if (!prefix) {
    client.invalidateQueries({ queryKey: ["engine"] });
    return;
  }
  for (const m of Array.isArray(prefix) ? prefix : [prefix]) client.invalidateQueries({ queryKey: ["engine", m] });
}

const CONTACT_METHODS: EngineMethodName[] = ["queryContacts", "getContactRows", "getContact", "getContactProfile", "getContactProfiles", "getContactHighlights", "getContactNotes", "getAllNotes", "getReminders", "listGroups", "getGroup", "getContactsInGroup", "getGroupsForContact", "getReconnectSuggestions", "getEnrichSuggestions", "getUpcomingBirthdays", "listPotentialDuplicates", "search", "getMapPins", "getConnectionGraph", "searchArchivedContacts", "getContactCount", "getContactLookups", "getRelatedContacts", "getTimeline", "getTimelineUnreadCount", "listNeeds", "listServices", "getContactTimeline", "getNoteLabels", "getMilestones", "getReconnectCadence", "getResolvedReconnectCadence", "getContactFieldValues", "getPendingReminders", "getUpcomingEvents", "getGroupHighlights"];

/** Mount once: turns engine notifications into query invalidation and toasts. */
export function useEngineNotifications(onNotification?: (n: EngineNotification) => void) {
  const qc = useQueryClient();
  useEffect(() => {
    return subscribeEngine((n) => {
      switch (n.method) {
        case "contactsChanged":
          CONTACT_METHODS.forEach((m) => qc.invalidateQueries({ queryKey: ["engine", m] }));
          break;
        case "timelineChanged":
          qc.invalidateQueries({ queryKey: ["engine", "getTimeline"] });
          qc.invalidateQueries({ queryKey: ["engine", "getTimelineUnreadCount"] });
          qc.invalidateQueries({ queryKey: ["engine", "getReminders"] });
          qc.invalidateQueries({ queryKey: ["engine", "getPendingReminders"] });
          break;
        case "remindersDue":
          qc.invalidateQueries({ queryKey: ["engine", "getReminders"] });
          qc.invalidateQueries({ queryKey: ["engine", "getPendingReminders"] });
          toast(n.params.reminderIds.length === 1 ? "A reminder is due" : `${n.params.reminderIds.length} reminders are due`);
          break;
        case "syncStatus":
          qc.setQueryData(engineKey("getSyncStatus"), n.params);
          break;
        case "duplicatesFound":
          qc.invalidateQueries({ queryKey: ["engine", "listPotentialDuplicates"] });
          break;
        case "importProgress":
          if (n.params.phase === "done") qc.invalidateQueries({ queryKey: ["engine"] });
          break;
        case "engineCrashed":
          toast(`Engine error in ${n.params.method}: ${n.params.message}`, "danger");
          break;
      }
      onNotification?.(n);
    });
  }, [qc, onNotification]);
}
